import { FC, useMemo } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import useModal from 'ui/partials/Modal/useModal';

import {
  useCreateEmailTemplate,
  useGetEmailTemplate,
  useUpdateEmailTemplate,
} from '../hooks';
import { EMAIL_TEMPLATE_FORM, EMAIL_TEMPLATE_SCHEMA } from '../forms/schema';
import EmailTemplateForm from '../forms/EmailTemplateForm';

type EmailTemplateModalProps = {
  id?: string;
  closeModal?: () => void;
};

const EmailTemplateModal: FC<EmailTemplateModalProps> = ({
  id,
  closeModal,
}) => {
  const { mutate: createEmailTemplate } = useCreateEmailTemplate();
  const { mutate: updateEmailTemplate } = useUpdateEmailTemplate();

  const { hideModal } = useModal();
  const { data: emailTemplateData } = useGetEmailTemplate(id ?? '');

  const handleClose = () => {
    if (closeModal) {
      closeModal();
    } else {
      hideModal();
    }
  };

  const onSubmit = (
    values: EMAIL_TEMPLATE_FORM,
    { setSubmitting }: FormikHelpers<EMAIL_TEMPLATE_FORM>,
  ) => {
    if (id) {
      updateEmailTemplate(
        { id, ...values },
        {
          onSettled: () => {
            setSubmitting(false);
            handleClose();
          },
        },
      );
    } else {
      createEmailTemplate(values, {
        onSettled: () => {
          setSubmitting(false);
          handleClose();
        },
      });
    }
  };

  const FORM_INIT: EMAIL_TEMPLATE_FORM = useMemo(() => {
    if (id && emailTemplateData) {
      return EMAIL_TEMPLATE_SCHEMA.noUnknown().cast(emailTemplateData);
    }
    return {
      name: '',
      subject: '',
      body: '',
      isShared: false,
    };
  }, [emailTemplateData, id]);
  return (
    <Formik
      initialValues={FORM_INIT}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={EMAIL_TEMPLATE_SCHEMA}
    >
      {() => (
        <EmailTemplateForm
          onCancel={() => {
            handleClose();
          }}
        />
      )}
    </Formik>
  );
};

export default EmailTemplateModal;
